// src/components/teacher/TeacherCourses.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders, handleUnauthorized } from "../../utils/api";
import { BookIcon, StudentsIcon, CalendarIcon } from "../shared/icons/Icons";
import { USE_DUMMY_DATA, DUMMY_STUDENTS } from "./dummyData";

/**
 * "My Courses" — one card per course the teacher is assigned to (course +
 * class + section). Clicking a card opens the course detail page where the
 * teacher can see the students of that class, its timetable and quick actions.
 */
function TeacherCourses({ courses = [], loading: parentLoading }) {
  const navigate = useNavigate();
  const [countsByClass, setCountsByClass] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(!USE_DUMMY_DATA);

  useEffect(() => {
    const fetchStudentCounts = async () => {
      if (USE_DUMMY_DATA) {
        const counts = {};
        DUMMY_STUDENTS.forEach((s) => {
          const key = s.class_name;
          counts[key] = (counts[key] || 0) + 1;
        });
        setCountsByClass(counts);
        setLoadingCounts(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/teachers/me/students`, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        });
        if (response.status === 401) {
          handleUnauthorized();
          return;
        }
        if (!response.ok) throw new Error("Failed to fetch students");
        const data = await response.json();
        const counts = {};
        (Array.isArray(data) ? data : []).forEach((s) => {
          if (s.class_id != null) {
            counts[s.class_id] = (counts[s.class_id] || 0) + 1;
          }
        });
        setCountsByClass(counts);
      } catch (err) {
        console.error("Failed to fetch student counts:", err);
      } finally {
        setLoadingCounts(false);
      }
    };

    fetchStudentCounts();
  }, []);

  const countFor = (course) => {
    if (USE_DUMMY_DATA) return countsByClass[course.class_name] || 0;
    return countsByClass[course.class_id] || 0;
  };

  const openCourse = (course) => {
    navigate(`/teacher/courses/${course.id}`, { state: { course } });
  };

  if (parentLoading || loadingCounts) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading your courses...</p>
      </div>
    );
  }

  return (
    <div className="tp-courses-page">
      <div className="tp-page-header">
        <div>
          <h2>
            <span className="tp-title-icon"><BookIcon /></span>
            <span>Courses</span>
          </h2>
          <p>Your assigned classes — open a course to manage students, remarks, homework, and files.</p>
        </div>
        <div className="tp-header-badges">
          <span className="tp-badge">{courses.length} Courses</span>
        </div>
      </div>

      {courses.length > 0 ? (
        <div className="tp-course-grid">
          {courses.map((course) => (
            <button
              key={course.id}
              type="button"
              className="tp-course-card"
              onClick={() => openCourse(course)}
            >
              <div className="tp-course-card__top">
                <span className="tp-course-card__icon">
                  <BookIcon />
                </span>
                <span className="tp-badge">
                  {countFor(course)} students
                </span>
              </div>
              <h3 className="tp-course-card__title">
                {course.course_name || course.name}
              </h3>
              <div className="tp-course-card__meta">
                <span className="tp-chip tp-chip--class">
                  {course.class_name || "No class"}
                </span>
                {course.class_section || course.section ? (
                  <span className="tp-chip tp-chip--section">
                    {course.class_section || course.section}
                  </span>
                ) : null}
              </div>
              {course.campus_name ? (
                <p className="tp-course-card__campus">{course.campus_name}</p>
              ) : null}
              <span className="tp-course-card__hint">
                Open course
                <span aria-hidden="true"> →</span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="tp-empty">
          <BookIcon />
          <h3>No courses assigned</h3>
          <p>You haven't been assigned to any courses yet.</p>
        </div>
      )}

      {courses.length > 0 ? (
        <div className="tp-actions-grid" style={{ marginTop: "1.5rem" }}>
          <button
            type="button"
            className="tp-action-btn"
            onClick={() => navigate("/teacher/students")}
          >
            <StudentsIcon />
            <span>All My Students</span>
          </button>
          <button
            type="button"
            className="tp-action-btn"
            onClick={() => navigate("/teacher/timetable")}
          >
            <CalendarIcon />
            <span>My Timetable</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default TeacherCourses;
